import {
  TransactionDetailItemStatus,
  TransactionSources,
  TransactionStatus,
  TransactionSteps,
  type TransactionDetailItemStatusType,
  type TransactionType,
  type TransactionDetailsItemType,
  type TransactionDetailsItemErrorDetailItemType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import { convertMapToOpts } from "../shared/index.ts";

export const getSourceTypeDisplayLabel = (
  source: TransactionType["source"]
) => {
  switch (source) {
    case "api":
      return "API";
    case "email":
      return "Email";
    case "iot":
      return "IoT";
    case "kafka":
      return "Queue";
    case "sftp":
      return "SFTP";
    case "upload":
      return "File Upload";
    case "unknown":
    default:
      return "Unknown";
  }
};

export const getStatusDisplayLabel = (status?: TransactionType["status"]) => {
  switch (status) {
    case "completed":
      return "Completed";
    case "completed_with_partial_success":
      return "Partial";
    case "failed":
      return "Failed";
    case "pending":
      return "In-Progress";
    default:
      return "Unknown";
  }
};

export const getStateDisplayLabel = (state: TransactionType["state"]) => {
  switch (state) {
    case "pending":
      return "In-Progress...";
    case "failed":
      return "Failed.";
    case "partial_failure":
      return "Partly failed.";
    case "partial_success":
      return "Partly succeeded.";
    case "retried":
      return "Retried.";
    case "success":
      return "Success.";
  }
};

/**
 * The order of operations for a dataset submission in the Data Platform API.
 * Some steps are aggregated together as one whole step for conciseness.
 */
export const DatasetSubmissionSteps = [
  [TransactionSteps.RECORDER, TransactionSteps.VULNERABILITY_SCANNER],
  TransactionSteps.ARCHIVER,
  TransactionSteps.OPERATOR,
  TransactionSteps.SINK,
];

export const getActiveStepIdx = (trx: TransactionType) => {
  return (
    DatasetSubmissionSteps.findIndex((stp) => {
      if (Array.isArray(stp)) {
        return stp.find((nestedStp) => {
          return nestedStp === trx.step;
        });
      }

      return stp === trx.step;
    }) + 1
  );
};

export const getStepDisplayLabel = (step: TransactionType["step"]) => {
  switch (step) {
    case "transaction_recorder":
    case "vulnerability_scanner":
      return "Import";
    case "archiver":
      return "Validation";
    case "operator":
      return "Transformation";
    case "sink":
    default:
      return "Ingestion";
  }
};

export const TransactionStatusOptions = convertMapToOpts(
  TransactionStatus,
  ([_, value]) => getStatusDisplayLabel(value)
);

export const TransactionSourceOptions = convertMapToOpts(
  TransactionSources,
  ([_, value]) => getSourceTypeDisplayLabel(value)
);

export const getTransactionDetailStatusesBasedOnStep = (
  step?: TransactionType["step"]
): TransactionDetailItemStatusType[] => {
  switch (step) {
    case "archiver":
      return [
        TransactionDetailItemStatus.SUCCESSFUL,
        TransactionDetailItemStatus.FAILED,
      ];
    default:
      return [
        TransactionDetailItemStatus.CREATED,
        TransactionDetailItemStatus.UPDATED,
        TransactionDetailItemStatus.FAILED,
        TransactionDetailItemStatus.UNCHANGED,
      ];
  }
};

export const getTransactionDetailStatusDisplayLabelBasedOnStep = (
  status?: TransactionDetailItemStatusType,
  step?: TransactionType["step"]
): string => {
  switch (step) {
    case "archiver": {
      switch (status) {
        case TransactionDetailItemStatus.SUCCESSFUL:
          return "Validated";
        case TransactionDetailItemStatus.FAILED:
          return "Failed";
        default:
          return "Skipped";
      }
    }
    case "operator":
    case "transaction_recorder":
    case "vulnerability_scanner":
    case "sink":
    default: {
      switch (status) {
        case TransactionDetailItemStatus.CREATED:
          return "Ingested";
        case TransactionDetailItemStatus.UPDATED:
        case TransactionDetailItemStatus.FAILED:
        case TransactionDetailItemStatus.UNCHANGED:
          return toTitleCaseString(status, { intent: "ui" });
        default:
          return "Not Ingested";
      }
    }
  }
};

export const getTransactionDetailStatusDisplayLabel = (
  status?: TransactionDetailItemStatusType
): string => {
  switch (status) {
    case TransactionDetailItemStatus.CREATED:
    case TransactionDetailItemStatus.SUCCESSFUL:
    case TransactionDetailItemStatus.UPDATED:
    case TransactionDetailItemStatus.FAILED:
    case TransactionDetailItemStatus.UNCHANGED:
      return toTitleCaseString(status, { intent: "ui" });
    default:
      return "Not Ingested";
  }
};

export type DataSetSubmissionTabularDataRowType = Omit<
  TransactionDetailsItemType,
  "index"
> & {
  index: number;
};

export type DataSetSubmissionTabularDataCellStyleVariantType =
  | "warning"
  | "error"
  | "info";

export const DataSetSubmissionTabularDataCellStyleVariantsMap: Record<
  | DataSetSubmissionTabularDataCellStyleVariantType
  | `${DataSetSubmissionTabularDataCellStyleVariantType}_dark`
  | `${DataSetSubmissionTabularDataCellStyleVariantType}_light`,
  string
> = {
  warning_dark: "#E37006",
  warning: "#FF8F27",
  warning_light: "#FFEEDF",
  error_dark: "#AF3032",
  error: "#D83B3E",
  error_light: "#FFE4E5",
  info_dark: "#3179E4",
  info: "#3179E4",
  info_light: "#E0EEFB",
};

export const getDataSetSubmissionTabularCellError = (
  rowDetails: DataSetSubmissionTabularDataRowType,
  columnLabelOrKey: string
): TransactionDetailsItemErrorDetailItemType | null => {
  if (rowDetails.error_details) {
    const richErrorDetails = rowDetails.error_details.find(
      (err) => err.field_name === columnLabelOrKey
    );

    if (richErrorDetails) return richErrorDetails;
  }

  const hasError = new RegExp(columnLabelOrKey).test(rowDetails.error_msg);

  return hasError
    ? {
        error_message: rowDetails.error_msg,
        field_name: columnLabelOrKey,
        validation_type: rowDetails.error,
        validation_value: [],
      }
    : null;
};

export const getWhichCellStyleVariantForCellInDataSetSubmissionTabularRow = (
  rowDetails: DataSetSubmissionTabularDataRowType,
  columnLabelOrKey: string
): DataSetSubmissionTabularDataCellStyleVariantType | "default" => {
  switch (rowDetails.status) {
    case "failed":
      return getDataSetSubmissionTabularCellError(rowDetails, columnLabelOrKey)
        ? "error"
        : "default";
    // REVIEW: these statuses don't have row/column level detail, so it will end up
    // styling all cells of that row. It would be nice to have that in the future.
    case "updated":
    case "created":
    case "unchanged":
    default:
      return "default";
  }
};

export const getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow = (
  rowDetails: DataSetSubmissionTabularDataRowType
): DataSetSubmissionTabularDataCellStyleVariantType | "default" => {
  switch (rowDetails.status) {
    case "failed":
      return "error";
    case "updated":
    case "created":
    case "unchanged":
    default:
      return "default";
  }
};

export const getReadableDataSetSubmissionErrorType = (
  errorType: DataSetSubmissionTabularDataRowType["error"]
) => {
  switch (errorType) {
    case "ValidationError":
      return "Validation Error";
    case "InvalidSchemaError":
      return "Invalid Format Error";
    case "RequestException":
      return "Database Error";
    case "AttributeError":
      return "Non-existent Attribute Error";
    case "MissingLookupError":
    case "MissingAnswerLookupError":
      return "Missing Answer Lookup Error";
    case "LookupCollisionError":
      return "Multiple Lookup Matches Error";
    default:
      return toTitleCaseString(errorType, { intent: "ui" });
  }
};

export const getFailedRowsFromTransactionDetails = (
  items: TransactionDetailsItemType[]
) =>
  items.filter(({ status }) => status === TransactionDetailItemStatus.FAILED);
